import {
  AI_IMAGE_MODEL_CARDS,
  AiImageAspectRatioOptions,
  AiImageFilterOptions,
  AiImageModelOption,
  AiImageStylesOptions,
  DEFAULT_SELECTED_AI_IMAGE_MODEL,
  MediaTypePrompt,
} from './ai-image-panel.types';
import {
  GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE,
  GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE,
} from '@Libraries/pmw-stock-media-library';
import type {ContextualMenuGroup} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {
  ContextualMenuGroupType,
  ContextualMenuItemGraphicType,
  type ContextualMenuItemWithEmoji,
  type ContextualMenuItemWithIcon,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import type {AiPromptBox as AiPromptBoxType} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {SkeletonAnimationType} from '@Components/base-grid/components/grid-skeleton-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {isUserPremium} from '@Libraries/user.library';
import {assertNever} from '@Utils/assert-never.util';
import type {AiImageGenerationState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';

export const AI_IMAGE_GENERATION_SKELETON_ID_PREFIX = 'ai-image-generation-skeleton-';
const SKELETON_ITEMS_PER_AI_IMAGE_GENERATION = 2;

export const getSkeletonIdsForAiImageGeneration = (generationId: string): string[] => {
  return Array.from({length: SKELETON_ITEMS_PER_AI_IMAGE_GENERATION}, (_, index) => `${AI_IMAGE_GENERATION_SKELETON_ID_PREFIX}${generationId}-${index}`);
};

export const getSkeletonGridItemsForAiImageGeneration = (generation: AiImageGenerationState): GridSkeletonItemStorage[] => {
  return getSkeletonIdsForAiImageGeneration(generation.id).map((id) => ({
    ...getAiSkeletonItem(id),
    ...getDimensionsForAiImageSkeletonItem(generation.aspectRatio),
    animationType: SkeletonAnimationType.AI,
  }));
};

/**
 * Inverts the `model.toString()` encoding the contextual menu stores its selection in. The menu
 * holds every value as a string and drops the group's key entirely when nothing is active, so an id
 * read back out of it is only trustworthy once it has matched a real enum member — anything else
 * would reach `getCreditCostForModel` and throw out of `assertNever`.
 */
export const getAiImageModelOptionFromMenuValue = (menuValue: string | undefined): AiImageModelOption => {
  const knownModel = Object.values(AiImageModelOption).find(
    (modelOption): modelOption is AiImageModelOption => typeof modelOption === 'number' && modelOption.toString() === menuValue
  );
  return knownModel ?? DEFAULT_SELECTED_AI_IMAGE_MODEL;
};

export const getDimensionsForAiImageSkeletonItem = (aspectRatio: AiImageAspectRatioOptions): Partial<GridSkeletonItemStorage> => {
  switch (aspectRatio) {
    case AiImageAspectRatioOptions.Portrait:
      return {width: 40};
    case AiImageAspectRatioOptions.LandScape:
      return {width: 122};
    case AiImageAspectRatioOptions.Square:
    default:
      return {width: 70};
  }
};

export const getAiSkeletonItem = (id: string): GridSkeletonItemStorage => {
  return {
    id: id,
    type: GRID_ITEM_TYPE.SKELETON,
    width: 180,
    height: 70,
    useFixedDimensions: false,
  };
};

/**
 * What to tell the user when the generate endpoint rejected their prompt, or null when the failure
 * was not a prompt rejection and is therefore worth logging.
 *
 * A rejected prompt reaches the client as the response code in the thrown error's message
 * (core.js `getErrorAjaxResponse` builds the Error from `response.message`, and the generate
 * endpoint answers with the exception's JSON error code). Anything unrecognised is a genuine
 * failure rather than something the user can reword their way out of.
 */
export const getPromptRejectionMessage = (errorMessage: string): string | null => {
  if (errorMessage.includes(GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE)) {
    return window.i18next.t('pmwjs_search_prompt_violation_message');
  }

  if (errorMessage.includes(GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE)) {
    return window.i18next.t('pmwjs_vague_prompt_error');
  }

  if (errorMessage.includes(GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE)) {
    return window.i18next.t('pmwjs_search_prompt_unsupported_language_message');
  }

  return null;
};

export const getCreditCostForModel = (value: AiImageModelOption): number => {
  switch (value) {
    case AiImageModelOption.StabilityAICore:
    case AiImageModelOption.StabilityAISDXL:
    case AiImageModelOption.DeepInfraFluxSchnell:
      return 1;
    case AiImageModelOption.Auto:
    case AiImageModelOption.Gemini2_5:
    case AiImageModelOption.ModelArkSeedream5_0Lite:
    case AiImageModelOption.DeepInfraSeedream4_5:
    case AiImageModelOption.DeepInfraFlux2Pro:
    case AiImageModelOption.GeminiNanoBanana2Lite:
      return 2;
    case AiImageModelOption.StabilityAIUltra:
      return 3;
    case AiImageModelOption.OpenAIGptImage2:
    case AiImageModelOption.GeminiNanoBanana2:
      return 4;
    default:
      return assertNever(value);
  }
};

export const getPromptBoxOptions = (
  mediaTypePrompt: MediaTypePrompt,
  model: AiImageModelOption,
  style: AiImageStylesOptions,
  aspectRatio: AiImageAspectRatioOptions
): AiPromptBoxType => {
  return {
    placeholder: getPlaceHolderText(mediaTypePrompt),
    showInitialAnimation: false,
    contextualMenu: getContextualMenuGroups(model, style, aspectRatio, mediaTypePrompt),
    size: AiPromptBoxSize.SMALL,
    creditsPill: {
      visible: false,
      actionCost: 0,
      remainingCredits: 1,
      showPremiumIcon: !isUserPremium(),
    },
    input: {
      hideScrollbar: true,
    },
    promptBoxState: PromptBoxState.IDLE,
    prompt: '',
  };
};

export const getPlaceHolderText = (mediaTypePrompt: MediaTypePrompt): string => {
  if (mediaTypePrompt === MediaTypePrompt.LOGO) {
    return window.i18next.t('pmwjs_describe_logo_for_ai_image_generation');
  }
  if (mediaTypePrompt === MediaTypePrompt.STICKER) {
    return window.i18next.t('pmwjs_describe_sticker_for_ai_image_generation');
  }
  if (mediaTypePrompt === MediaTypePrompt.TEXT_ART) {
    return window.i18next.t('pmwjs_describe_text_art_for_ai_image_generation');
  }
  return window.i18next.t('pmwjs_describe_image_for_ai_image_generation');
};

export const getContextualMenuGroups = (
  model: AiImageModelOption,
  style: AiImageStylesOptions,
  aspectRatio: AiImageAspectRatioOptions,
  mediaTypePrompt: MediaTypePrompt
): ContextualMenuGroup[] => {
  return [
    {
      label: window.i18next.t('pmwjs_style'),
      key: AiImageFilterOptions.Styles,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: window.i18next.t('pmwjs_photographic'),
          value: AiImageStylesOptions.Photographic,
          active: style === AiImageStylesOptions.Photographic,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '📷',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_digital_art'),
          value: AiImageStylesOptions.DigitalArt,
          active: style === AiImageStylesOptions.DigitalArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '💻',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_three_d_model'),
          value: AiImageStylesOptions.Model3d,
          active: style === AiImageStylesOptions.Model3d,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '📦',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_cinematic'),
          value: AiImageStylesOptions.Cinematic,
          active: style === AiImageStylesOptions.Cinematic,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🎬',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_futuristic'),
          value: AiImageStylesOptions.NeonPunk,
          active: style === AiImageStylesOptions.NeonPunk,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🤖',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_line_art'),
          value: AiImageStylesOptions.LineArt,
          active: style === AiImageStylesOptions.LineArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '✏️',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_fantasy'),
          value: AiImageStylesOptions.FantasyArt,
          active: style === AiImageStylesOptions.FantasyArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🐉',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_anime'),
          value: AiImageStylesOptions.Anime,
          active: style === AiImageStylesOptions.Anime,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '👩‍🎤',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_pixel_art'),
          value: AiImageStylesOptions.PixelArt,
          active: style === AiImageStylesOptions.PixelArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '👾',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_vintage'),
          value: AiImageStylesOptions.AnalogFilm,
          active: style === AiImageStylesOptions.AnalogFilm,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '📻',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_comic'),
          value: AiImageStylesOptions.ComicBook,
          active: style === AiImageStylesOptions.ComicBook,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🦸🏻‍♂️',
        } as ContextualMenuItemWithEmoji,
      ],
    },
    {
      label: window.i18next.t('pmwjs_aspect_ratio'),
      key: AiImageFilterOptions.AspectRatio,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: window.i18next.t('pmwjs_square'),
          value: AiImageAspectRatioOptions.Square,
          active: aspectRatio === AiImageAspectRatioOptions.Square,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-square',
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_landscape'),
          value: AiImageAspectRatioOptions.LandScape,
          active: aspectRatio === AiImageAspectRatioOptions.LandScape,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-landscape',
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_portrait'),
          value: AiImageAspectRatioOptions.Portrait,
          active: aspectRatio === AiImageAspectRatioOptions.Portrait,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-portrait',
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        } as ContextualMenuItemWithIcon,
      ],
    },
    {
      label: window.i18next.t('pmwjs_ai_model'),
      key: AiImageFilterOptions.AiModel,
      type: ContextualMenuGroupType.CARDS,
      items: AI_IMAGE_MODEL_CARDS.map((card) => ({
        label: window.i18next.t(card.labelKey),
        value: card.model.toString(),
        active: model === card.model,
        description: window.i18next.t(card.descriptionKey),
        topRightIcon: 'icon-credits',
        credits: getCreditCostForModel(card.model),
      })),
    },
  ];
};
